import { addClass } from '@opten/dom/add-class';
import { hasClass } from '@opten/dom/has-class';
import { removeClass, removeClassOnCollection } from '@opten/dom/remove-class';
import { toggleClass } from '@opten/dom/toggle-class';
import { scrollToOffset } from '@opten/scroll/scroll-to-offset';
import { addTransitionEndListener } from '@opten/transition/add-transition-end-listener';
import { debounce } from '@opten/reactive/debounce';

export interface TheWallOptions {
	onBeforeInit?: () => void;
	onReady?: () => void;
	onViewPortUpdate?: () => void;
	onItemChange?: () => void;
	onDestroy?: () => void;
	onShow?: () => void;
	onHide?: () => void;
	onContentLoading?: () => void;
	onContentLoaded?: () => void;
	margin?: TheWallOptionsMargin;
	scrollerElm?: HTMLElement;
	scrollOffset?: number;
	arrows?: boolean;
	closebutton?: boolean;
	keyboardNavigation?: boolean;
	animationSpeed?: number;
	autoScrollSpeed?: number;
	autoscroll?: boolean;
	responsive?: boolean;
	initialWrapperHeight?: number;
	dynamicHeight?: boolean;
	listSelector?: string;
	listItemSelector?: string;
	hiddenClass?: string;
	expanderClass?: string;
	expanderInnerClass?: string;
	pointerClass?: string;
	loadingClass?: string;
	fullImgClass?: string;
	indexClass?: string;
	rowClass?: string;
	selectedRowClass?: string;
	controlsClass?: string;
	closeClass?: string;
	arrowClass?: string;
	arrowNextClass?: string;
	arrowPrevClass?: string;
	nextButtonClass?: string;
	prevButtonClass?: string;
	closeButtonClass?: string;
}
export interface TheWallOptionsMargin {
	top?: number;
	bottom?: number;
}
export interface TheWallOptionsEventNames {
	click: string;
}

export class TheWall {
	expanderWrapper: HTMLElement;
	contentPointer: HTMLElement;
	expanderInner: HTMLElement;
	nextArrow: HTMLElement;
	prevArrow: HTMLElement;
	list: HTMLElement;
	items: HTMLElement[];
	itemsLength: number;
	currentIndex: number;
	currentRowIndex: number;
	selectedli: HTMLElement;

	protected theWallId = 0;
	protected isOpened = false;
	protected isLoading = false;
	protected cachedWrapperHeight = 0;

	protected options: TheWallOptions = {
		onBeforeInit: null,
		onReady: null,
		onViewPortUpdate: null,
		onItemChange: null,
		onDestroy: null,
		onShow: null,
		onHide: null,
		onContentLoading: null,
		onContentLoaded: null,
		margin: {
			top: 10,
			bottom: 10,
		},
		scrollerElm: null,
		scrollOffset: 150,
		arrows: true,
		closebutton: true,
		keyboardNavigation: true,
		animationSpeed: 300,
		autoScrollSpeed: 500,
		autoscroll: true,
		responsive: true,
		initialWrapperHeight: 600,
		dynamicHeight: true,
		listSelector: '.c-the-wall__list',
		listItemSelector: '.c-the-wall__list__item',
		hiddenClass: 'd-none',
		expanderClass: 'c-the-wall__expander',
		expanderInnerClass: 'c-the-wall__expander__inner',
		pointerClass: 'c-the-wall__expander__pointer',
		loadingClass: 'c-the-wall__loading',
		fullImgClass: 'c-the-wall__fullimg',
		indexClass: 'c-the-wall--index',
		rowClass: 'c-the-wall--row',
		selectedRowClass: 'is-selected-row',
		controlsClass: 'c-the-wall__controls',
		closeClass: 'c-the-wall__close',
		arrowClass: 'c-the-wall__arrow',
		arrowNextClass: 'c-the-wall__arrow__next',
		arrowPrevClass: 'c-the-wall__arrow__prev',
		nextButtonClass: '',
		prevButtonClass: '',
		closeButtonClass: '',
	};

	constructor(public element: HTMLElement, options?: TheWallOptions) {
		this.expanderWrapper = document.createElement('div');
		this.expanderInner = document.createElement('div');
		this.contentPointer = document.createElement('div');
		addClass(this.expanderWrapper, this.options.expanderClass);
		addClass(this.expanderInner, this.options.expanderInnerClass);
		addClass(this.contentPointer, this.options.pointerClass);
		this.options = {
			...this.options,
			...options,
		};

		this._init();
	}

	setLisOffsets(): void {
		let cachedOffsetTop = 0;
		let rowIndex = 0;
		let previousLi;
		let i = 0;
		for (const item of this.items) {
			removeClass(item, 'is-first');
			removeClass(item, 'is-last');
			removeClass(
				item,
				this.options.indexClass + item.getAttribute('data-index')
			);
			removeClass(
				item,
				this.options.rowClass + item.getAttribute('data-row-index')
			);
			let isFirst = false;
			const liPosition = {
				top: item.offsetTop,
				left: item.offsetLeft,
			};
			if (
				liPosition.top >= cachedOffsetTop + 3 ||
				liPosition.top <= cachedOffsetTop - 3
			) {
				if (previousLi) {
					addClass(previousLi, 'is-last');
				}
				isFirst = true;
				rowIndex++;
			}
			if (isFirst) {
				addClass(item, 'is-first');
			}
			addClass(item, this.options.indexClass + i);
			addClass(item, this.options.rowClass + rowIndex);
			item.setAttribute('data-row-index', rowIndex.toString());
			item.setAttribute('data-offset-top', liPosition.top.toString());
			item.setAttribute('data-offset-left', liPosition.left.toString());
			item.setAttribute('data-index', i.toString());
			previousLi = item;
			cachedOffsetTop = liPosition.top;
			i++;
		}
	}

	setViewport() {
		if (!this.isOpened) {
			return;
		}
		this.setLisOffsets();

		this.updateElementsPosition();

		this._updateContentPointerPosition();

		this._execCallback(this.options.onViewPortUpdate);
	}

	loadInnerContents() {
		const a = this.selectedli.getElementsByTagName('a')[0];
		let callback;
		const href =
			this.selectedli.getAttribute('data-href') || a.getAttribute('href');
		this.isLoading = true;
		addClass(this.expanderWrapper, 'is-loading');
		this.expanderInner.innerHTML =
			'<div class="' + this.options.loadingClass + '"></div>';

		if (!href) {
			Error('sorry.. it was not possible to load any content');
			return;
		} else {
			this._execCallback(this.options.onContentLoading);
			switch (this.selectedli.getAttribute('data-contenttype')) {
				case 'image':
					callback = this._loadImage(href);
					break;
				default:
					callback = this._loadInlineContent(href);
					break;
			}

			callback.then(() => {
				removeClass(this.expanderWrapper, 'is-loading');
				this.expanderInner.style.height = 'auto';

				const newHeight = this.options.dynamicHeight
					? this.expanderInner.offsetHeight
					: this.options.initialWrapperHeight;
				this._updateExpanderWrapperHeight(newHeight);
				this.update().then(() => {
					this._scrollTo(
						this.expanderWrapper.getBoundingClientRect().top -
							this.options.scrollOffset
					);
					this._execCallback(this.options.onContentLoaded);
					this.isLoading = false;
				});
			});
		}
	}

	showExpander(e) {
		e.preventDefault();
		this.selectedli = e.currentTarget;
		if (this.selectedli && hasClass(this.selectedli, 'is-current')) {
			this.hideExpander();
			return;
		}

		if (this.isOpened) {
			this._onItemChange.call(this);
			return;
		}

		this.isOpened = true;

		addClass(this.expanderWrapper, 'is-open');

		this.setViewport();
		this._onItemChange();

		this._execCallback(this.options.onShow);
	}

	hideExpander() {
		removeClass(this.expanderWrapper, 'is-open');
		const oldHeight = this.cachedWrapperHeight;
		this.expanderWrapper.style.height = '0';

		this.expanderInner.innerHTML = '';

		this.currentRowIndex = null;
		this.selectedli = null;
		this.currentIndex = null;
		this.cachedWrapperHeight = 0;

		for (const item of this.items) {
			removeClass(item, this.options.selectedRowClass);
			item.style.marginBottom = '0';
		}

		addClass(this.expanderWrapper, 'is-decaying');
		setTimeout(() => {
			removeClass(this.expanderWrapper, 'is-decaying');
		}, 300);

		this.isOpened = false;

		this._updateCurrentClass();

		this._execCallback(this.options.onHide);
	}

	refresh() {
		this.list = this.element.querySelector(this.options.listSelector);
		this.items = Array.from(
			this.list.querySelectorAll(this.options.listItemSelector)
		) as HTMLElement[];
		if (this.selectedli.parentNode !== this.list) {
			this.hideExpander();
		}
		this.itemsLength = this.items.length;
		if (this.isOpened) {
			this.setLisOffsets();
			this.update();
		}
	}

	update() {
		return new Promise((resolve) => {
			this._updateContentPointerPosition();

			if (
				parseInt(this.selectedli.getAttribute('data-row-index'), 0) !==
				this.currentRowIndex
			) {
				this.updateElementsPosition().then(() => {
					if (this.options.arrows) {
						this._updateArrows();
					}

					this._updateCurrentClass();

					this.currentRowIndex = parseInt(
						this.selectedli.getAttribute('data-row-index'),
						0
					);
					resolve();
				});
			} else {
				if (this.options.arrows) {
					this._updateArrows();
				}

				this._updateCurrentClass();

				this.currentRowIndex = parseInt(
					this.selectedli.getAttribute('data-row-index'),
					0
				);
				resolve();
			}
		});
	}

	updateElementsPosition() {
		// robin
		this.updateExpanderPosition();

		return new Promise((resolve) => {
			resolve();
			// let counter = 0;
			for (const item of this.items) {
				const pushIt =
					item.getAttribute('data-row-index') ===
					this.selectedli.getAttribute('data-row-index');
				toggleClass(item, this.options.selectedRowClass, pushIt);
				// addTransitionEndListener(item, () => {
				// 	counter++;
				// 	if (counter >= this.itemsLength) {

				// 		// this.updateExpanderPosition();
				// 		// this.setLisOffsets();
				// 		resolve();
				// 	}
				// }, 500);
				if (pushIt) {
					item.style.marginBottom =
						this.cachedWrapperHeight + this.options.margin.bottom + 'px';
				} else {
					item.style.marginBottom = '0';
				}
			}
		});
	}

	updateExpanderPosition() {
		let newTopPosition = this.selectedli.offsetHeight + this.options.margin.top;
		if (!this.isOpened) {
			return;
		}
		if (this.selectedli.getAttribute('data-offset-top')) {
			newTopPosition =
				parseInt(this.selectedli.getAttribute('data-offset-top'), 0) +
				newTopPosition;
		}
		this.expanderWrapper.style.top = newTopPosition + 'px';
	}

	resizeHeight() {
		this.expanderInner.style.height = 'auto';
		const newHeight = this.options.dynamicHeight
			? this.expanderInner.offsetHeight
			: this.options.initialWrapperHeight;
		this._updateExpanderWrapperHeight(newHeight);
		this.setViewport();
	}

	showItemByIndex(index) {
		const currentLi = this.items[index];
		if (currentLi) {
			this.selectedli = currentLi;
			this._onItemChange();
		}
	}

	next() {
		if (
			this.isLoading ||
			!this.isOpened ||
			this.currentIndex === this.itemsLength - 1
		) {
			return;
		}
		let index;
		if (this.currentIndex + 1 === this.itemsLength - 1) {
			index = this.currentIndex + 1;
		} else {
			index = this.items
				.slice(this.currentIndex + 1, this.itemsLength - 1)
				.find((o) => o.style.display !== 'none')
				.getAttribute('data-index');
		}
		this.showItemByIndex(parseInt(index, 0));
	}

	prev() {
		if (this.isLoading || !this.isOpened || this.currentIndex === 0) {
			return;
		}
		const index = this.items
			.slice(0, this.currentIndex)
			.reverse()
			.find((o) => o.style.display !== 'none')
			.getAttribute('data-index');
		this.showItemByIndex(parseInt(index, 0));
	}

	bindAll() {
		addTransitionEndListener(
			this.expanderWrapper,
			() => {
				removeClass(this.expanderWrapper, 'is-animating');
			},
			undefined,
			false
		);

		// FIXME: Do we really need this options? maybe better we support arrows if arrows are added to the html
		if (this.options.arrows) {
			// CHANGED: for of NodeListOf<Element> does not work in IE! changed to Array
			Array.from(
				this.expanderWrapper.getElementsByClassName(this.options.arrowClass)
			).forEach((item) =>
				item.addEventListener('click', (e) => {
					this._onArrowClicked(e);
				})
			);
		}

		if (this.options.closebutton) {
			Array.from(
				this.expanderWrapper.getElementsByClassName(this.options.closeClass)
			).forEach((item) =>
				item.addEventListener('click', () => {
					this.hideExpander();
				})
			);
		}

		Array.from(
			this.element.querySelectorAll(
				this.options.listSelector + ' ' + this.options.listItemSelector
			)
		).forEach((item) =>
			item.addEventListener('click', (e) => {
				this.showExpander(e);
			})
		);

		if (this.options.responsive) {
			window.addEventListener('resize', () => {
				debounce(() => this.resizeHeight(), 300);
			});
			window.addEventListener('orientationchange', () => {
				debounce(() => this.resizeHeight(), 300);
			});
		}
		if (this.options.keyboardNavigation) {
			window.addEventListener('keydown', (e) => {
				this._onKeypress(e);
			});
		}
	}

	unbindAll() {
		if (this.options.arrows) {
			Array.from(
				this.expanderWrapper.getElementsByClassName(this.options.arrowClass)
			).forEach((item) =>
				item.removeEventListener('click', (e) => {
					this._onArrowClicked(e);
				})
			);
		}

		if (this.options.closebutton) {
			Array.from(
				this.expanderWrapper.getElementsByClassName(this.options.closeClass)
			).forEach((item) =>
				item.removeEventListener('click', () => {
					this.hideExpander();
				})
			);
		}

		Array.from(
			this.element.querySelectorAll(
				this.options.listSelector + ' ' + this.options.listItemSelector
			)
		).forEach((item) =>
			item.removeEventListener('click', (e) => {
				this.showExpander(e);
			})
		);

		if (this.options.responsive) {
			window.removeEventListener('resize', () => {
				debounce(() => this.resizeHeight(), 300);
			});
			window.removeEventListener('orientationchange', () => {
				debounce(() => this.resizeHeight(), 300);
			});
		}
		if (this.options.keyboardNavigation) {
			window.removeEventListener('keydown', (e) => {
				this._onKeypress(e);
			});
		}
	}

	destroy(e) {
		if (e) {
			e.preventDefault();
			e.stopImmediatePropagation();
		}
		this.hideExpander();

		this.unbindAll();
		this.expanderWrapper.remove();
		this._execCallback(this.options.onDestroy);
	}

	private _execCallback<T>(callback: (e: T) => void, arg?: T): void {
		if (typeof callback === 'function') {
			callback(arg);
		}
	}

	private _buildArrows(): void {
		const nextElem = document.createElement('div');
		const prevElem = document.createElement('div');
		const nextElemSpan = document.createElement('span');
		const prevElemSpan = document.createElement('span');
		addClass(nextElem, this.options.arrowClass);
		addClass(nextElem, this.options.arrowNextClass);
		addClass(nextElem, this.options.controlsClass);
		addClass(prevElem, this.options.arrowClass);
		addClass(prevElem, this.options.arrowPrevClass);
		addClass(prevElem, this.options.controlsClass);
		addClass(prevElemSpan, this.options.prevButtonClass);
		addClass(nextElemSpan, this.options.nextButtonClass);
		prevElem.appendChild(prevElemSpan);
		nextElem.appendChild(nextElemSpan);
		this.expanderWrapper.appendChild(prevElem);
		this.expanderWrapper.appendChild(nextElem);
		this.nextArrow = this.expanderWrapper.getElementsByClassName(
			this.options.arrowNextClass
		)[0] as HTMLElement;
		this.prevArrow = this.expanderWrapper.getElementsByClassName(
			this.options.arrowPrevClass
		)[0] as HTMLElement;
	}

	private _updateArrows(): void {
		toggleClass(this.prevArrow, this.options.hiddenClass, this._hasNoPrev());
		toggleClass(this.nextArrow, this.options.hiddenClass, this._hasNoNext());
	}

	private _hasNoNext(): boolean {
		const hasNoNext = this.currentIndex >= this.itemsLength - 1;
		if (this.currentIndex === undefined || hasNoNext === true) {
			return hasNoNext;
		}
		if (this.items[this.currentIndex + 1].style.display !== 'none') {
			return hasNoNext;
		}
		return (
			this.items
				.slice(this.currentIndex + 1, this.itemsLength - 1)
				.find((o) => o.style.display !== 'none') === undefined
		);
	}

	private _hasNoPrev(): boolean {
		const hasNoPrev = this.currentIndex <= 0;
		if (this.currentIndex === undefined || hasNoPrev === true) {
			return hasNoPrev;
		}
		if (this.items[this.currentIndex - 1].style.display !== 'none') {
			return hasNoPrev;
		}
		return (
			this.items
				.slice(0, this.currentIndex)
				.reverse()
				.find((o) => o.style.display !== 'none') === undefined
		);
	}

	private _scrollTo(value: number) {
		if (this.options.autoscroll === false) {
			return false;
		}
		scrollToOffset(value, undefined, this.options.autoScrollSpeed);
	}

	private _updateCurrentClass(): void {
		removeClassOnCollection(this.items, 'is-current');
		if (typeof this.currentIndex === 'number') {
			addClass(this.items[this.currentIndex], 'is-current');
		}
	}

	private _updateContentPointerPosition(): void {
		const offset =
			parseInt(this.selectedli.getAttribute('data-offset-left'), 0) +
			this.element.getBoundingClientRect().left;
		if (offset) {
			this.contentPointer.style.left =
				offset + this.selectedli.offsetWidth / 2 + 'px';
		} else {
			this.contentPointer.style.left = this.selectedli.offsetWidth / 2 + 'px';
		}
	}

	private _loadInlineContent(selector): Promise<void> {
		return new Promise((resolve, reject) => {
			const el = document.querySelector(selector);
			if (!el) {
				reject(
					Error('No element can be found using the "' + selector + '" selector')
				);
			}
			this.expanderInner.innerHTML = el.innerHTML;
			resolve();
		});
	}

	private _loadImage(src): Promise<void> {
		return new Promise((resolve, reject) => {
			let img = new Image();
			img.onload = () => {
				this.expanderInner.innerHTML =
					'<div class="' +
					this.options.fullImgClass +
					'"><img src="' +
					src +
					'" /></div>';
				resolve();
				img = null;
			};

			img.src = src;
		});
	}

	private _updateExpanderWrapperHeight(newHeight): void {
		this.expanderInner.style.height = newHeight + 'px';
		if (newHeight === this.cachedWrapperHeight) {
			return;
		}
		const oldHeight = this.cachedWrapperHeight;
		this.cachedWrapperHeight = newHeight;
		addClass(this.expanderWrapper, 'is-animating');

		this.expanderWrapper.style.height = newHeight + 'px';

		this.updateElementsPosition();
	}

	private _onItemChange(): void {
		const index = parseInt(this.selectedli.getAttribute('data-index'), 0);
		if (index < 0 || index >= this.itemsLength) {
			return;
		}

		this.currentIndex = index;

		this.loadInnerContents();

		this._execCallback(this.options.onItemChange, this.currentIndex);
	}

	private _onKeypress(e): void {
		if (!this.isOpened) {
			return;
		}
		if (e.target.form !== undefined) {
			return;
		}
		if (e.target.isContentEditable) {
			return;
		}
		if ([33, 34, 35, 36, 37, 38, 39, 40, 27].indexOf(e.keyCode) > -1) {
			e.preventDefault();
		}
		if (e.keyCode === 39) {
			this.next();
		} else if (e.keyCode === 37) {
			this.prev();
		} else if (e.keyCode === 27) {
			this.hideExpander();
		}
	}

	private _onArrowClicked(e): void {
		e.stopImmediatePropagation();
		e.preventDefault();
		if (hasClass(e.currentTarget, this.options.arrowNextClass)) {
			this.next();
		} else {
			this.prev();
		}
	}

	private _init(): void {
		this._execCallback(this.options.onBeforeInit);

		if (this.options.arrows) {
			this._buildArrows();
			this._updateArrows();
		}

		if (this.options.closebutton) {
			const close = document.createElement('div');
			const closeSpan = document.createElement('span');
			addClass(close, this.options.closeClass);
			addClass(close, this.options.controlsClass);
			addClass(closeSpan, this.options.closeButtonClass);
			close.appendChild(closeSpan);
			this.expanderWrapper.appendChild(close);
		}
		this.expanderWrapper.appendChild(this.contentPointer);
		this.expanderWrapper.appendChild(this.expanderInner);
		this.element.innerHTML =
			this.expanderWrapper.outerHTML + this.element.innerHTML;
		this.expanderWrapper = this.element.getElementsByClassName(
			this.options.expanderClass
		)[0] as HTMLElement;
		this.expanderInner = this.expanderWrapper.getElementsByClassName(
			this.options.expanderInnerClass
		)[0] as HTMLElement;
		this.contentPointer = this.expanderWrapper.getElementsByClassName(
			this.options.pointerClass
		)[0] as HTMLElement;
		this.nextArrow = this.expanderWrapper.getElementsByClassName(
			this.options.arrowNextClass
		)[0] as HTMLElement;
		this.prevArrow = this.expanderWrapper.getElementsByClassName(
			this.options.arrowPrevClass
		)[0] as HTMLElement;
		this.list = this.element.querySelector(
			this.options.listSelector
		) as HTMLElement;
		this.items = Array.from(
			this.list.querySelectorAll(this.options.listItemSelector)
		) as HTMLElement[];
		this.itemsLength = this.items.length;

		this.bindAll();

		this._execCallback(this.options.onReady);

		this.theWallId++;
	}
}

export function theWall(
	element: HTMLElement,
	options: TheWallOptions
): TheWall {
	return new TheWall(element, options);
}
